// ✅ src/pages/ArchiveDebiteurs.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaSpinner,
  FaSearch,
  FaInfoCircle,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./ClientsDebiteurs.css"; // On garde le même style
import API_URL from "../config";

export default function ArchiveDebiteurs({ entrepriseId, siteId }) {
  const [debiteurs, setDebiteurs] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [infoData, setInfoData] = useState(null);

  const [clientsList, setClientsList] = useState([]);
  const [produitsList, setProduitsList] = useState([]);
  const [denominationsList, setDenominationsList] = useState([]);

  const [filters, setFilters] = useState({
    client: "",
    produit: "",
    denomination1: "",
    denomination2: "",
    dateDebut: "",
    dateFin: "",
  });

  const wrapperRef = useRef(null);

  // -----------------------------
  // 🔸 Chargement depuis l'API /list
  // -----------------------------
  useEffect(() => {
    if (!entrepriseId || !siteId) return;
    const controller = new AbortController();

    const loadDebiteurs = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/api/archive-debiteurs/list?entrepriseId=${entrepriseId}&siteId=${siteId}`
,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Erreur serveur lors du chargement");

        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];

        setDebiteurs(arr);
        setGrouped(groupByClient(arr));

        // Listes pour filtres
        setClientsList([...new Set(arr.map((d) => d.client).filter(Boolean))]);
        setProduitsList([...new Set(arr.map((d) => d.produit_nom).filter(Boolean))]);
        const uniqueDenoms = [
          ...new Set(
            arr
              .map((d) => (d.denomination_stock || "").split("(")[0].trim())
              .filter(Boolean)
          ),
        ];
        setDenominationsList(uniqueDenoms);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err);
          setError("Impossible de charger les clients débiteurs archivés.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDebiteurs();
    return () => controller.abort();
  }, [entrepriseId, siteId]);

  // -----------------------------
  // 🔸 Groupement par client
  // -----------------------------
  const groupByClient = (list) => {
    const map = {};
    list.forEach((item) => {
      const client = item.client || "—";
      if (!map[client]) map[client] = [];
      map[client].push(item);
    });
    return Object.entries(map).map(([client, items]) => ({ client, items }));
  };

  // -----------------------------
  // 🔸 Application des filtres
  // -----------------------------
  const [filteredDebiteurs, setFilteredDebiteurs] = useState([]);
  const applyFilters = () => {
    let filtered = [...debiteurs];

    if (filters.client)
      filtered = filtered.filter((d) => d.client === filters.client);
    if (filters.produit)
      filtered = filtered.filter((d) => d.produit_nom === filters.produit);

    const { denomination1, denomination2 } = filters;
    if (denomination1 && !denomination2) {
      filtered = filtered.filter((d) =>
        (d.denomination_stock || "").startsWith(denomination1)
      );
    }
    if (!denomination1 && denomination2) {
      filtered = filtered.filter((d) =>
        (d.denomination_stock || "").startsWith(denomination2)
      );
    }
    if (denomination1 && denomination2) {
      filtered = filtered.filter(
        (d) =>
          (d.denomination_stock || "").startsWith(denomination1) ||
          (d.denomination_stock || "").startsWith(denomination2)
      );
    }

    const { dateDebut, dateFin } = filters;
    if (dateDebut) filtered = filtered.filter((d) => d.date_vente >= dateDebut);
    if (dateFin) filtered = filtered.filter((d) => d.date_vente <= dateFin);

    if (search) {
      const val = search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          (d.client || "").toLowerCase().includes(val) ||
          (d.produit_nom || "").toLowerCase().includes(val) ||
          (d.denomination_stock || "").toLowerCase().includes(val) ||
          String(d.quantite).includes(val) ||
          String(d.montant).includes(val)
      );
    }

    setFilteredDebiteurs(filtered);
    setGrouped(groupByClient(filtered));
  };

  useEffect(() => {
    applyFilters();
  }, [filters, search, debiteurs]);

  // -----------------------------
  // 🔸 Scroll horizontal
  // -----------------------------
  const scrollBy = (amount = 300) => {
    const el = wrapperRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollLeft(el.scrollLeft > 10);
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 10);
    };
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [grouped]);

  // -----------------------------
  // 🔸 Rendu principal
  // -----------------------------
  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <FaSpinner className="spin" /> Chargement…
      </div>
    );
  if (error) return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;

  return (
    <div className="clients-debiteurs">
      <div className="header">
        <h3>Clients débiteurs archivés</h3>
        <div className="search-box">
          <FaSearch className="icon" />
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* 🔸 Filtres */}
      <div className="filters">
        <FaFilter /> <span>Filtres :</span>

        <select
          value={filters.client}
          onChange={(e) => setFilters({ ...filters, client: e.target.value })}
        >
          <option value="">Tous les clients</option>
          {clientsList.map((c, i) => (
            <option key={i} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={filters.produit}
          onChange={(e) => setFilters({ ...filters, produit: e.target.value })}
        >
          <option value="">Tous les produits</option>
          {produitsList.map((p, i) => (
            <option key={i} value={p}>
              {p}
            </option>
          ))}
        </select>

        <select
          value={filters.denomination1}
          onChange={(e) =>
            setFilters({ ...filters, denomination1: e.target.value })
          }
        >
          <option value="">Dénomination 1</option>
          {denominationsList.map((d, i) => (
            <option key={i} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={filters.denomination2}
          onChange={(e) =>
            setFilters({ ...filters, denomination2: e.target.value })
          }
        >
          <option value="">Dénomination 2</option>
          {denominationsList.map((d, i) => (
            <option key={i} value={d}>
              {d}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={filters.dateDebut}
          onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })}
        />
        <input
          type="date"
          value={filters.dateFin}
          onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })}
        />
      </div>

      {/* 🔸 Tableau */}
      {grouped.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: 20 }}>
          Aucun client débiteur archivé trouvé.
        </p>
      ) : (
        <div className="debiteurs-table-wrapper" ref={wrapperRef}>
          {canScrollLeft && (
            <button
              className="scroll-btn scroll-left"
              onClick={() => scrollBy(-320)}
              aria-label="Scroll left"
            >
              <FaChevronLeft />
            </button>
          )}
          {canScrollRight && (
            <button
              className="scroll-btn scroll-right"
              onClick={() => scrollBy(320)}
              aria-label="Scroll right"
            >
              <FaChevronRight />
            </button>
          )}

          <table className="debiteurs-table" role="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Produit</th>
                <th>Dénomination</th>
                <th>Quantité</th>
                <th>Montant (FCFA)</th>
                <th>Paiement partiel</th>
                <th>Crédit restant</th>
                <th>Date vente</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g, gi) => (
                <React.Fragment key={gi}>
                  {g.items.map((item, idx) => (
                    <tr key={item.id || `${g.client}-${idx}`}>
                      {idx === 0 && (
                        <td rowSpan={g.items.length} className="client-col">
                          {g.client}
                        </td>
                      )}
                      <td>{item.produit_nom || "—"}</td>
                      <td>{item.denomination_stock || "—"}</td>
                      <td>{item.quantite}</td>
                      <td style={{ color: "green", fontWeight: "bold" }}>
                        {item.montant}
                      </td>
                      <td>
                        {item.paiement_partielle ?? 0}
                        {item.paiement_partielle > 0 && (
                          <FaInfoCircle
                            title="Détails paiement partiel"
                            className="info-icon"
                            style={{ marginLeft: 6 }}
                            onClick={() => setInfoData(item)}
                          />
                        )}
                      </td>
                      <td style={{ color: "red", fontWeight: "bold" }}>
                        {item.credit_restant ?? 0}
                      </td>
                      <td>
                        {item.date_vente
                          ? new Date(item.date_vente).toLocaleDateString("fr-FR")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  <tr className="client-separator">
                    <td colSpan="8"></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals Info */}
      {infoData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Détails paiement partiel</h4>
            <p>
              <strong>Date paiement :</strong>{" "}
              {infoData.date_paiement_partielle
                ? new Date(infoData.date_paiement_partielle).toLocaleDateString(
                    "fr-FR"
                  )
                : "—"}
            </p>
            <p>
              <strong>Moyen :</strong> {infoData.moyen_paiement || "—"}
            </p>
            <div className="modal-actions">
              <button onClick={() => setInfoData(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
